import { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
    AttributeOption,
    CautionsTabLocationsView,
    DispositionsForm,
    EventPanelFormType,
    ConsolidatedLocationView,
} from '@mark43/cad-api';
import { FrPage } from '../../fr-page-layout/fr-page';
import { FrEventPageHeader } from '../../fr-event-common-components/fr-event-page-header';
import { FrPageBody } from '../../fr-page-layout/fr-page-body';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { DispositionForm } from '../../dispositions/components/disposition-form';
import { FrDispositionSummarySection } from '../../fr-event-common-components/fr-disposition/components/fr-disposition-summary-section';
import { UseFormReturn } from 'react-hook-form';
import { FrPageFooter } from '../../fr-page-layout/fr-page-footer';
import { Flex } from '@cad-arc';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { useDispositionQuery } from '../../dispositions/hooks/use-disposition-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSaveMutationErrorMessage } from '../../common/hooks/use-save-mutation-error-message';
import { useOpenFrEventDetails } from '../../fr-event-details-page/hooks/use-open-fr-event-details';
import { FR_EVENT_DETAILS_TABS } from '../../fr-event-details-page/constants';

type FrDispositionPageContentProps = {
    isClearEventPage?: boolean;
    isUnassignCurrentUserUnitPage?: boolean;
    isEditDispositionPage?: boolean;
    eventId?: number;
    unitId?: number;
    isPrimaryUnit: boolean;
    saveMutationErrorMsg: string;
    dispositionOptions?: AttributeOption[];
    agencyId: number;
    agencyTypeGlobalAttrId: number;
    cautions?: CautionsTabLocationsView;
    areCautionsLoading: boolean;
    eventLocation: ConsolidatedLocationView;
    eventForm: UseFormReturn<EventPanelFormType>;
    primaryDispositionEditable?: boolean;
};

export const FrDispositionPageContent = observer(function FrDispositionPageContent({
    isClearEventPage,
    isUnassignCurrentUserUnitPage,
    isEditDispositionPage,
    eventId,
    unitId,
    isPrimaryUnit,
    saveMutationErrorMsg,
    dispositionOptions,
    agencyId,
    agencyTypeGlobalAttrId,
    cautions,
    areCautionsLoading,
    eventLocation,
    eventForm,
    primaryDispositionEditable,
}: FrDispositionPageContentProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const openFrEventDetails = useOpenFrEventDetails();
    const saveMutationErrorMessage = useSaveMutationErrorMessage(saveMutationErrorMsg);

    // Cancel returns the user to where they launched this flow from (the event).
    // When opened directly by URL there is no in-app history to pop
    // (location.key === 'default'), so fall back to the event details (Narrative tab).
    const handleCancel = () => {
        if (location.key === 'default') {
            openFrEventDetails(eventId, FR_EVENT_DETAILS_TABS.NARRATIVE);
        } else {
            navigate(-1);
        }
    };

    const dispositionQuery = useDispositionQuery({
        agencyEventId: eventId,
    });

    const { isFetching, data } = dispositionQuery;

    const initialValues: DispositionsForm = useMemo(
        () => ({
            ...data?.form,
            agencyEventId: eventId ?? 0,
            additionalDispositions: data?.form.additionalDispositions ?? [],
            requestingUnitId: unitId,
            isRequestingUnitPrimaryUnit: isPrimaryUnit,
        }),
        [data, eventId, unitId, isPrimaryUnit]
    );

    return (
        <FrPage>
            <FrEventPageHeader
                isClearEventPage={isClearEventPage}
                isUnassignCurrentUserUnitPage={isUnassignCurrentUserUnitPage}
                isEditDispositionPage={isEditDispositionPage}
            />
            <FrPageBody
                customStyles={{
                    padding: '24px',
                    overflowY: 'auto',
                }}
            >
                <FormBody
                    saveErrorMessage={saveMutationErrorMessage}
                    formBodyStyle={{ width: '50%', marginBottom: '48px' }}
                >
                    <DispositionForm
                        isEventClearing={isClearEventPage}
                        isLoading={isFetching}
                        agencyId={agencyId}
                        isPrimaryUnit={isPrimaryUnit}
                        dispositionOptions={dispositionOptions ?? []}
                        initialValues={initialValues}
                        primaryDispositionEditable={primaryDispositionEditable}
                    />
                </FormBody>
                <FrDispositionSummarySection
                    cautions={cautions}
                    areCautionsLoading={areCautionsLoading}
                    eventLocation={eventLocation}
                    eventForm={eventForm}
                    agencyTypeGlobalAttrId={agencyTypeGlobalAttrId}
                />
            </FrPageBody>
            <FrPageFooter customStyles={{ justifyContent: 'flex-end' }}>
                <Flex gap={4}>
                    <FormCancelButton size="xl" onClick={handleCancel} />
                    <FormSaveButton size="xl" />
                </Flex>
            </FrPageFooter>
        </FrPage>
    );
});
